import type { EntityData } from '@adornis/baseql/entities/types.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { alertDialog, guardDialog } from '@adornis/dialog/dialog-functions.js';
import { FileInput } from '@adornis/file-utils/client/file-input.js';
import { AdornisFile, AdornisFileMeta } from '@adornis/file-utils/db/files.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { GlobalConfigAPI } from '@martinus/global-config/api/GlobalConfigAPI.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { html, nothing } from 'lit';
import { Bundesland } from '../../../../db/enums/misc-enums.js';
import { useMartinusABAC } from '../../../../permissions/client/useMartinusAbac.js';
import type { Person } from '../../../entities/person.js';
import type { UserOnUserPayload } from '../../../UserPolicy.js';
import { DownloadEmployeeFormTemplates, downloadEmployeeFormTemplates } from '../../downloadEmployeeFormTemplates.js';
import { UserEmployeeDetailsActions } from '../../UserEmployeeDetailsPolicy.js';

async function downloadTemplate(template: DownloadEmployeeFormTemplates, userID: string) {
  await guardDialog(
    async () => {
      const file = await downloadEmployeeFormTemplates(
        template,
        userID,
      )({ _id: 1, meta: selectionSet(() => AdornisFileMeta, 1) });
      goTo(AdornisFile.getDownloadLink(file._id), { target: '_blank' });
    },
    { errorMessagePrefix: 'Fehler beim Herunterladen des Dokuments' },
  );
}

export const EmployeeSectionDokumenteForm = stateful((props: { form: FunctionalForm<EntityData<Person>> }) => {
  const viewedUser = props.form.document;

  const { can: canViewRecruitmentDocuments } = useMartinusABAC<UserOnUserPayload>({
    action: UserEmployeeDetailsActions.VIEW_RECRUITMENT_DOCUMENTS,
    viewedUser,
  });

  const { can: canViewComplianceDocuments } = useMartinusABAC<UserOnUserPayload>({
    action: UserEmployeeDetailsActions.VIEW_COMPLIANCE_DOCUMENTS,
    viewedUser,
  });

  const { can: canViewInternalEmploymentDocuments } = useMartinusABAC<UserOnUserPayload>({
    action: UserEmployeeDetailsActions.VIEW_INTERNAL_EMPLOYMENT_DOCUMENTS,
    viewedUser,
  });

  const { can: canViewFuehrungszeugnis } = useMartinusABAC<UserOnUserPayload>({
    action: UserEmployeeDetailsActions.VIEW_FUEHRUNGSZEUGNIS,
    viewedUser,
  });

  return html`
    <div class="form-grid">
      <p class="col-span-full mb-6">
        Nachstehende Dokumente sind nicht alle relevant, sie können in der entsprechenden Rubrik hochgeladen werden. Die
        mit einem '*' gekennzeichneten Dokumente müssen jedoch zwingend zur Verfügung gestellt werden.
      </p>

      ${
        canViewRecruitmentDocuments
          ? html`
              <h4 class="col-span-full mt-6 border-b border-base-300 pb-1 font-semibold">CV & Zeugnisse</h4>
              ${FormGridEntry({
                label: 'Lebenslauf',
                render: FileInput({
                  ...props.form.field('mitarbeiterDaten', 'anDokumente', 'lebenslaufID'),
                  placeholder: 'Kein Lebenslauf hinterlegt',
                  placeholderMode: 'hide',
                }),
              })}
              ${FormGridEntry({
                label: 'Zeugnisse',
                render: FileInput({
                  ...props.form.field('mitarbeiterDaten', 'anDokumente', 'zeugnisseIDs'),
                  placeholder: 'Keine Zeugnisse hinterlegt',
                  placeholderMode: 'hide',
                  multi: true,
                }),
              })}
            `
          : nothing
      }
      ${
        canViewComplianceDocuments
          ? html`
              <h4 class="col-span-full mt-6 border-b border-base-300 pb-1 font-semibold">Führungszeugnis</h4>
              ${FormGridEntry({
                label: 'Erklärung Strafverfahren',
                labelSubtext: html`"Erklärung Strafverfahren"-Formular
                  <a
                    class="link link-primary"
                    @click=${() =>
                      viewedUser._id && downloadTemplate(DownloadEmployeeFormTemplates.STRAFVERFAHREN, viewedUser._id)}
                  >
                    herunterladen
                  </a>`,
                render: FileInput({
                  ...props.form.field('mitarbeiterDaten', 'anDokumente', 'erklaerungStrafverfahrenID'),
                  placeholder: 'Keine Erklärung Strafverfahren hinterlegt',
                  placeholderMode: 'hide',
                }),
              })}
              ${FormGridEntry({
                label: 'Führungszeugnis',
                labelSubtext: html`Führungszeugnis Antrag
                  <a
                    class="link link-primary"
                    @click=${() =>
                      viewedUser._id &&
                      downloadTemplate(DownloadEmployeeFormTemplates.ANTRAGFUEHRUNGSZEUGNISS, viewedUser._id)}
                  >
                    herunterladen </a
                  >. Wenn Sie mehr ale ein Führungszeugnis hochladen möchten, fügen Sie die Dokumente bitte zusammen.`,
                render: html`
                  <div class="flex flex-row gap-2">
                    ${
                      canViewFuehrungszeugnis
                        ? FileInput({
                            ...props.form.field('mitarbeiterDaten', 'anDokumente', 'fuehrungszeugnisID'),
                            placeholder: 'Kein Führungszeugnis hinterlegt',
                            placeholderMode: 'hide',
                          })
                        : nothing
                    }
                    ${DateInput({
                      ...props.form.field('mitarbeiterDaten', 'anDokumente', 'fuehrungszeugnisAusstellung'),
                      placeholder: 'Ausstellungsdatum',
                      placeholderMode: 'prefix',
                    })}
                  </div>
                `,
              })}

              <h4 class="col-span-full mt-6 border-b border-base-300 pb-1 font-semibold">Gesundheitsschutz</h4>
              <p class="col-span-full text-sm text-base-content/70">
                Das Infoblatt zum Infektionsschutz können Sie
                <a
                  class="link link-primary"
                  @click=${async () => {
                    const config = await GlobalConfigAPI.get()({ infectionInformationPaper: 1 });
                    if (!config.infectionInformationPaper) {
                      await alertDialog(
                        'Das Infektionsschutzpapier ist nicht hinterlegt. Bitte Kontaktieren Sie einen Administrator.',
                      );
                      return;
                    }
                    goTo(AdornisFile.getServeLink(config.infectionInformationPaper), { target: '_blank' });
                  }}
                  >hier</a
                >
                herunterladen.
              </p>
              ${FormGridEntry({
                label: 'Gesundheitszeugnis',
                labelSubtext: html`Den Antrag für eine ärztliche Bescheinigung
                  <a
                    class="link link-primary"
                    @click=${() =>
                      viewedUser._id &&
                      downloadTemplate(DownloadEmployeeFormTemplates.GESUNDHEITSZEUGNIS, viewedUser._id)}
                    >herunterladen</a
                  >`,
                render: FileInput({
                  ...props.form.field('mitarbeiterDaten', 'anDokumente', 'gesundheitszeugnisID'),
                  placeholder: 'Kein Gesundheitszeugnis hinterlegt',
                  placeholderMode: 'hide',
                }),
              })}
              ${FormGridEntry({
                label: 'Infektionsschutz',
                labelSubtext: html`Das Infektionsschutz-Formular
                  <a
                    class="link link-primary"
                    @click=${() =>
                      viewedUser._id &&
                      downloadTemplate(DownloadEmployeeFormTemplates.INFEKTIONSSCHUTZ, viewedUser._id)}
                    >herunterladen</a
                  >`,
                render: FileInput({
                  ...props.form.field('mitarbeiterDaten', 'anDokumente', 'infektionsschutzID'),
                  placeholder: 'Kein Infektionsschutz hinterlegt',
                  placeholderMode: 'hide',
                }),
              })}
              ${FormGridEntry({
                label: 'Masernimpfung',
                render: FileInput({
                  ...props.form.field('mitarbeiterDaten', 'anDokumente', 'masernimpfungID'),
                  placeholder: 'Kein Nachweis der Masernimpfung hinterlegt',
                  placeholderMode: 'hide',
                }),
              })}
              <h4 class="col-span-full mt-6 border-b border-base-300 pb-1 font-semibold">Sonstige Dokumente</h4>
              ${FormGridEntry({
                label: 'Personalbogen',
                labelSubtext: html`Das Personalbogen-Formular
                  <a
                    class="link link-primary"
                    @click=${() =>
                      viewedUser._id && downloadTemplate(DownloadEmployeeFormTemplates.PERSONLABOGEN, viewedUser._id)}
                    >herunterladen</a
                  >`,
                render: FileInput({
                  ...props.form.field('mitarbeiterDaten', 'anDokumente', 'personalbogenID'),
                  placeholder: 'Kein Personalbogen hinterlegt',
                  placeholderMode: 'hide',
                }),
              })}
              ${FormGridEntry({
                label: 'Datenschutz',
                render: FileInput({
                  ...props.form.field('mitarbeiterDaten', 'anDokumente', 'datenschutzID'),
                  placeholder: 'Kein Datenschutz hinterlegt',
                  placeholderMode: 'hide',
                }),
              })}
              ${FormGridEntry({
                label: 'Selbstverpflichtungserklärung',
                render: FileInput({
                  ...props.form.field('mitarbeiterDaten', 'anDokumente', 'selbstversplichtungserklaerungID'),
                  placeholder: 'Keine Selbstverpflichtungserklärung hinterlegt',
                  placeholderMode: 'hide',
                }),
              })}
              ${FormGridEntry({
                label: 'Versorgeaufwendungen',
                render: FileInput({
                  ...props.form.field('mitarbeiterDaten', 'anDokumente', 'vorsorgeaufwendungenID'),
                  placeholder: 'Keine Vorsorgeaufwendungen hinterlegt',
                  placeholderMode: 'hide',
                }),
              })}
              ${FormGridEntry({
                label: 'Vermögenswirksame Leistungen',
                render: FileInput({
                  ...props.form.field('mitarbeiterDaten', 'anDokumente', 'vermoegensLeistungenID'),
                  placeholder: 'Keine Vermögenswirksame Leistungen hinterlegt',
                  placeholderMode: 'hide',
                }),
              })}
              ${FormGridEntry({
                label: 'Studienbescheinigungen',
                render: FileInput({
                  ...props.form.field('mitarbeiterDaten', 'anDokumente', 'studienbescheinigungsIDs'),
                  placeholder: 'Keine Studienbescheinigungen hinterlegt',
                  placeholderMode: 'hide',
                  multi: true,
                }),
              })}
            `
          : nothing
      }
      ${
        canViewRecruitmentDocuments
          ? html`
              <h4 class="col-span-full mt-6 border-b border-base-300 pb-1 font-semibold">MAV Dokumente</h4>
              ${FormGridEntry({
                label: 'Beschäftigungsgenehmigungen',
                render: FileInput({
                  ...props.form.field('mitarbeiterDaten', 'anDokumente', 'beschaeftigungsgenehmigungIDs'),
                  placeholder: 'Keine Beschäftigungsgenehmigungen hinterlegt',
                  placeholderMode: 'hide',
                  multi: true,
                }),
              })}
            `
          : nothing
      }
      ${
        canViewInternalEmploymentDocuments
          ? html`
              <h4 class="col-span-full mt-6 border-b border-base-300 pb-1 font-semibold">Arbeitsverträge & Sonstige</h4>
              ${FormGridEntry({
                label: 'Arbeitsverträge',
                render: FileInput({
                  ...props.form.field('mitarbeiterDaten', 'anDokumente', 'arbeitsvertragIDs'),
                  placeholder: 'Keine Arbeitsverträge hinterlegt',
                  placeholderMode: 'hide',
                  multi: true,
                }),
              })}
              ${FormGridEntry({
                label: 'Nachträge',
                render: FileInput({
                  ...props.form.field('mitarbeiterDaten', 'anDokumente', 'nachtragIDs'),
                  placeholder: 'Keine Nachträge hinterlegt',
                  placeholderMode: 'hide',
                  multi: true,
                }),
              })}
              ${FormGridEntry({
                label: 'Sonstige',
                render: FileInput({
                  ...props.form.field('mitarbeiterDaten', 'anDokumente', 'sonstigeIDs'),
                  placeholder: 'Keine sonstigen Dokumente hinterlegt',
                  placeholderMode: 'hide',
                  multi: true,
                }),
              })}
            `
          : nothing
      }
    </div>
  `;
});

export const EmployeeSectionDokumenteFormValidator = (bundesland?: Bundesland) => ({
  masernimpfungID: options => {
    const user = options.target;
    // born after 1970 - the last calendar day of 1970, canonically encoded
    const compareDate = new Date(Date.UTC(1970, 11, 31));
    if (
      user.profilDaten?.stammdaten?.geburtsDatum &&
      user.profilDaten.stammdaten.geburtsDatum > compareDate &&
      !options.value
    )
      throw new ValidationError('Bei Personen geboren nach 1970 ist eine Masernimpfung erforderlich!', {
        key: 'zeugnisseIDs',
        path: options.path,
      });
  },
  zeugnisseIDs: options => {
    if (!options.value || options.value.length === 0) {
      throw new ValidationError('Mindestens ein Zeugnis ist erforderlich.', {
        key: 'zeugnisseIDs',
        path: options.path,
      });
    }
    if (options.value.some(v => !v)) {
      throw new ValidationError('Stellen Sie sicher, dass alle erstellten Zeugnisfelder befüllt sind.', {
        key: 'zeugnisseIDs',
        path: options.path,
      });
    }
  },
  lebenslaufID: sgNonOptional(),
  erklaerungStrafverfahrenID: sgNonOptional(),
  fuehrungszeugnisAusstellung: sgNonOptional(),
  personalbogenID: options => {
    if (bundesland !== Bundesland.RHEINLAND_PFALZ) return;
    return sgNonOptional()(options);
  },
});
